import { useEffect, useState } from 'react';

/**
 * Hook que detecta si el dispositivo es móvil basado en el user agent.
 * Esta detección es más estable que usar window.innerWidth.
 */
export const useIsMobileDevice = (): boolean => {
  const [isMobile] = useState<boolean>(() => {
    const userAgent =
      navigator.userAgent ||
      navigator.vendor ||
      (typeof window !== 'undefined' &&
        (window as Window & { opera?: string }).opera) ||
      '';
    const mobileRegex =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    return mobileRegex.test(String(userAgent).toLowerCase());
  });

  return isMobile;
};

/**
 * Hook que detecta si la ventana es móvil basado en el ancho.
 * Útil si quieres responsive design en lugar de detección de dispositivo.
 */
export const useIsMobileScreen = (breakpoint: number = 768): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    return typeof window !== 'undefined' && window.innerWidth < breakpoint;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
};

/**
 * Hook combinado que detecta tanto dispositivo como tamaño de pantalla.
 * Recomendado para máxima compatibilidad.
 */
export const useIsMobile = (breakpoint: number = 768): boolean => {
  const isMobileDevice = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen(breakpoint);
  return isMobileDevice || isMobileScreen;
};
